/**
 * Native ATD, Scheduling's side (April Torino, 2026-10): Finance marks a
 * resident approved to dispense; Scheduling makes sure they are on a manifest
 * or gets them a pit stop, and records the dispense. This queue is every
 * resident waiting, by building, with where each stands on the schedule. The
 * ATD status is Finance's; planning here records only where the resident will
 * be seen. Shown only what the server allows: plan actions need `can.plan`,
 * marking dispensed needs `can.dispense`, and each building's own `can` (the
 * queue can show buildings the caller may read but not act on).
 */
import React, { useCallback, useEffect, useState } from 'react';
import { outcomeOf, shortDate } from '../finance/financeApi';
import BuildingOrClinic from '../../components/emerge/BuildingOrClinic';
import { atdApi, newAtdKey, ATD_ATTENTION_LABEL, AtdBuilding, AtdQueueData, AtdResident, AtdPlanBody } from './atdApi';

type Mode = 'manifest' | 'pit' | 'dispense';
/** The Kanban's words for a closure kind ("Closed for scheduling (dropped)"). */
const CLOSURE_WORDS: Record<string, string> = { DROPPED: 'dropped', DO_NOT_SCHEDULE: 'do not schedule' };

function ScheduleBadge({ resident }: { resident: AtdResident }) {
  const s = resident.schedule;
  const text = s.state === 'ON_MANIFEST' ? `On manifest ${shortDate(s.visit_on)}`
    : s.state === 'PIT_STOP' ? `Pit stop ${shortDate(s.pit_stop_on)}`
      : 'Not yet scheduled';
  return (
    <>
      {/* A plan that no longer holds is never shown in the "done" colour. */}
      <span className={`sc-tag${s.state === 'NOT_PLANNED' || s.attention ? ' attn' : ' ok'}`}>{text}</span>
      {s.attention && <span className="sc-src attn" role="note">{ATD_ATTENTION_LABEL[s.attention]}</span>}
      {s.note && <span className="sc-src">{s.note}</span>}
    </>
  );
}

function ResidentRow({ resident, building, asOf, canPlan, canDispense, onDone }: {
  resident: AtdResident; building: AtdBuilding; asOf: string; canPlan: boolean; canDispense: boolean;
  onDone: (msg: string) => Promise<void>;
}) {
  const [mode, setMode] = useState<Mode | null>(null);
  const [visitId, setVisitId] = useState('');
  const [pitDate, setPitDate] = useState('');
  const [note, setNote] = useState('');
  const [dispDate, setDispDate] = useState(asOf);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const open = (m: Mode) => { setMode(mode === m ? null : m); setErr(null); };
  const send = async (call: () => ReturnType<typeof atdApi.plan>, ok: string) => {
    setBusy(true); setErr(null);
    try {
      const r = await call();
      if (!r.success) { setErr(r.status === 404 && !r.error && !r.message ? 'That change is not switched on yet.' : outcomeOf(r, { wrote: true })); return; }
      setMode(null);
      await onDone(ok);
    } catch { setErr('Could not reach the server. Nothing was recorded.'); } finally { setBusy(false); }
  };
  const plan = (body: AtdPlanBody, ok: string) => send(() => atdApi.plan(resident.patient_id, body, newAtdKey()), ok);

  const submitManifest = (e: React.FormEvent) => { e.preventDefault(); if (visitId) plan({ state: 'ON_MANIFEST', visit_plan_id: visitId }, `${resident.name} is on the manifest.`); };
  const submitPit = (e: React.FormEvent) => { e.preventDefault(); if (pitDate) plan({ state: 'PIT_STOP', pit_stop_on: pitDate, note: note.trim() || undefined }, `${resident.name} has a pit stop planned.`); };
  const submitDispense = (e: React.FormEvent) => {
    e.preventDefault();
    send(() => atdApi.dispense(resident.patient_id, dispDate, newAtdKey()) as ReturnType<typeof atdApi.plan>, `${resident.name} is marked dispensed.`);
  };

  return (
    <li className="sc-atd-row" aria-label={`Resident ${resident.name}`}>
      <div className="sc-atd-main">
        <strong>{resident.name}</strong>
        <span className="um-quiet"> · Room {resident.room || '—'} · ATD {shortDate(resident.atd_date)}{resident.days_waiting !== null ? ` · ${resident.days_waiting} ${resident.days_waiting === 1 ? 'day' : 'days'} waiting` : ''}</span>
        {!building.facility && (
          <div className="um-quiet" data-testid="atd-resident-building">Building: <BuildingOrClinic row={resident} fallback="not on file" /></div>
        )}
        <div><ScheduleBadge resident={resident} /></div>
      </div>
      {(canPlan || canDispense) && (
        <div className="sc-cmds">
          {canPlan && !building.closure && <button type="button" className={`um-open${mode === 'manifest' ? ' um-primary' : ''}`} onClick={() => open('manifest')} aria-label={`Put ${resident.name} on manifest`}>Put on manifest</button>}
          {canPlan && !building.closure && <button type="button" className={`um-open${mode === 'pit' ? ' um-primary' : ''}`} onClick={() => open('pit')} aria-label={`Plan pit stop for ${resident.name}`}>Plan pit stop</button>}
          {canPlan && (resident.schedule.recorded_state || resident.schedule.state) !== 'NOT_PLANNED' && <button type="button" className="um-open" disabled={busy} onClick={() => plan({ state: 'NOT_PLANNED' }, `${resident.name}'s plan was cleared.`)} aria-label={`Clear plan for ${resident.name}`}>Clear plan</button>}
          {canDispense && <button type="button" className={`um-open${mode === 'dispense' ? ' um-primary' : ''}`} onClick={() => open('dispense')} aria-label={`Mark ${resident.name} dispensed`}>Mark dispensed</button>}
        </div>
      )}
      {mode === 'manifest' && (
        <form className="um-form" onSubmit={submitManifest} aria-label={`Put ${resident.name} on manifest form`}>
          {building.open_visits.length === 0
            ? <p className="um-quiet">This building has no open visit to put {resident.name} on. Plan a visit first, or plan a pit stop.</p>
            : <label><span>Visit</span>
              <select value={visitId} onChange={(e) => setVisitId(e.target.value)} aria-label="Visit">
                <option value="">Choose a visit</option>
                {building.open_visits.map((v) => <option key={v.id} value={v.id}>{shortDate(v.planned_on)} · {v.state === 'CONFIRMED' ? 'Confirmed' : 'Planned'}{v.provider ? ` · ${v.provider}` : ''}</option>)}
              </select></label>}
          <div className="btns"><button type="submit" className="um-open um-primary" disabled={busy || !visitId}>{busy ? 'Recording…' : 'Confirm: put on manifest'}</button></div>
        </form>
      )}
      {mode === 'pit' && (
        <form className="um-form" onSubmit={submitPit} aria-label={`Plan pit stop for ${resident.name} form`}>
          <label><span>Pit stop date</span><input type="date" min={asOf} value={pitDate} onChange={(e) => setPitDate(e.target.value)} aria-label="Pit stop date" /></label>
          <label><span>Note (optional)</span><input value={note} maxLength={500} onChange={(e) => setNote(e.target.value)} aria-label="Pit stop note" /></label>
          <div className="btns"><button type="submit" className="um-open um-primary" disabled={busy || !pitDate}>{busy ? 'Recording…' : 'Confirm: plan pit stop'}</button></div>
        </form>
      )}
      {mode === 'dispense' && (
        <form className="um-form" onSubmit={submitDispense} aria-label={`Mark ${resident.name} dispensed form`}>
          <label><span>Dispensed on</span><input type="date" max={asOf} value={dispDate} onChange={(e) => setDispDate(e.target.value)} aria-label="Dispensed on" /></label>
          <div className="btns"><button type="submit" className="um-open um-primary" disabled={busy || !dispDate}>{busy ? 'Recording…' : 'Confirm: mark dispensed'}</button></div>
        </form>
      )}
      {err && <p className="um-notice stop" role="alert">{err}</p>}
    </li>
  );
}

export default function AtdQueue({ onNotice }: { onNotice?: (msg: string | null) => void }) {
  const [data, setData] = useState<AtdQueueData | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoadErr(null);
    try {
      const r = await atdApi.queue();
      if (r.success && r.data) { setData(r.data); return; }
      // Never an empty queue on a failure: that would read as "nobody is waiting".
      setData(null);
      setLoadErr(r.code === 'ATD_MIGRATION_REQUIRED' ? (r.message || r.error || 'ATD is not available until the database update is applied.') : outcomeOf(r));
    } catch { setData(null); setLoadErr('Could not reach the server.'); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const done = async (msg: string) => { setNotice(msg); onNotice?.(null); await load(); };

  if (loadErr) return <p className="um-notice stop" role="alert">{loadErr} <button type="button" className="um-open" onClick={load}>Try again</button></p>;
  if (!data) return <p className="um-quiet">Loading…</p>;

  return (
    <div className="um-list">
      <p className="um-quiet">Finance or UMED marks a resident approved to dispense; Scheduling makes sure they are on a manifest or gets them a pit stop. {data.can.plan ? '' : 'Read-only: planning is not open for this account.'}</p>
      {notice && <p className="um-notice ok" role="status">{notice}</p>}
      {data.counts.total === 0 || data.buildings.length === 0
        ? <p className="um-quiet">No residents are waiting to be dispensed.</p>
        : (
          <>
            <p className="um-quiet">{data.counts.total} waiting · {data.counts.NOT_PLANNED} not yet scheduled · {data.counts.ON_MANIFEST} on a manifest · {data.counts.PIT_STOP} pit stop{data.counts.attention ? ` · ${data.counts.attention} need attention` : ''}</p>
            {data.buildings.map((b) => {
              const name = b.facility ? b.facility.name : 'No building on file';
              return (
                <section key={b.facility ? b.facility.id : 'none'} className="um-card" aria-label={name}>
                  <h3>{name}</h3>
                  <p className="um-quiet">
                    {b.next_visit ? `Next visit ${shortDate(b.next_visit.on)}` : 'No visit planned'}{b.provider ? ` · ${b.provider}` : ''} · {b.residents.length} {b.residents.length === 1 ? 'resident' : 'residents'}
                  </p>
                  {b.closure && <p className="um-notice" role="note">Closed for scheduling ({CLOSURE_WORDS[b.closure.kind || ''] || b.closure.kind || 'reason not recorded'}): no resident goes on a visit&rsquo;s manifest or gets a pit stop here; a plan can still be cleared.</p>}
                  <ul className="sc-atd-list">
                    {b.residents.map((r) => <ResidentRow key={r.patient_id} resident={r} building={b} asOf={data.as_of} canPlan={data.can.plan && (!b.can || b.can.plan)} canDispense={data.can.dispense && (!b.can || b.can.dispense)} onDone={done} />)}
                  </ul>
                </section>
              );
            })}
          </>
        )}
    </div>
  );
}
